// Auth en Capacitor (Android/iOS).
//
// En web, signInWithOAuth redirige el navegador y vuelve a window.location.origin.
// Dentro del WebView nativo eso no funciona: el redirect de Google/Supabase no
// sabe cómo volver a la app. La solución es un deep link (esquema custom) que
// Android enruta de vuelta a MainActivity, donde interceptamos la URL y
// canjeamos el `code` (flujo PKCE) por una sesión.
//
// Estos imports tienen implementación web (no-op / stubs), así que el bundle
// web los tolera sin romperse; isNative es false fuera del contenedor nativo.
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { supabase } from './supabase';
import { isNative, platform } from './platform';

export { isNative };

// Debe coincidir con el intent-filter de AndroidManifest.xml y estar en la
// lista de Redirect URLs de Supabase (Auth → URL Configuration).
export const OAUTH_CALLBACK = 'cl.mipae.app://login-callback';

// Inicia el login con Google. En web mantiene el comportamiento de siempre;
// en nativo abre el navegador del sistema y vuelve por el deep link.
export async function signInWithGoogle() {
  if (!isNative) {
    return supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
  }

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: OAUTH_CALLBACK, skipBrowserRedirect: true },
  });
  if (error) return { data, error };
  if (data?.url) await Browser.open({ url: data.url });
  return { data, error: null };
}

// Registra el listener del deep link de retorno. Devuelve una función de
// cleanup. No-op en web.
export function initDeepLinkAuth() {
  if (!isNative) return () => {};

  let handlePromise;
  try {
    handlePromise = CapApp.addListener('appUrlOpen', async ({ url }) => {
      if (!url || !url.startsWith(OAUTH_CALLBACK)) return;
      try {
        const parsed = new URL(url);
        const code = parsed.searchParams.get('code');
        if (code) {
          // PKCE: el code verifier quedó en localStorage al iniciar el login.
          await supabase.auth.exchangeCodeForSession(code);
        }
      } catch {
        // Si falla, onAuthStateChange no dispara y el usuario reintenta el login.
      } finally {
        try { await Browser.close(); } catch { /* el browser ya pudo cerrarse */ }
      }
    });
  } catch {
    // addListener no debería lanzar, pero si el plugin no está registrado no
    // queremos tumbar el arranque: el deep link simplemente no se engancha.
    return () => {};
  }

  return () => { handlePromise.then((h) => h.remove()).catch(() => {}); };
}

// ─── Iniciar sesión con Apple ────────────────────────────────────────────────
//
// POR QUÉ EXISTE (2026-09-16). Apple rechazó la 1.3.0 por la guideline 4.8: una
// app que deja entrar con un servicio de terceros (Google) tiene que ofrecer al
// lado otra puerta que limite los datos a nombre y correo, que deje al usuario
// OCULTAR su correo real y que no rastree para publicidad. El código por correo
// no cumple la segunda (necesitamos el correo real para mandar el código), así
// que la única puerta que Apple acepta como equivalente es la suya.
//
// Solo en la app de iPhone: la regla es de la App Store, y el flujo web de
// Apple exige un "Services ID" y una llave rotativa que no hacen falta para el
// flujo nativo. En web y Android el botón no aparece y nada cambia.
//
// Cómo funciona: el sistema abre la hoja nativa de Apple (huella o cara), Apple
// devuelve un identityToken firmado y Supabase lo canjea por una sesión con
// signInWithIdToken. Supabase valida el token contra el bundle id, que tiene
// que estar en los "Client IDs" del proveedor Apple del proyecto.
export const APPLE_LOGIN_DISPONIBLE = isNative && platform === 'ios';

// Google NO se ofrece en la app de iOS (2026-10-02, cuarto rechazo de la 1.3.0).
// El revisor entró con Apple sin problemas y después apretó "Continuar con
// Google" siete veces: las siete salieron hacia Google y NINGUNA volvió a la app
// (los auth_logs muestran el /authorize con 302 y cero /callback). Es el mismo
// cuadro del 18-sep, en el iPad donde Apple revisa también las apps de iPhone,
// y no tenemos iPad para probar un arreglo. En iPhone Google sí funciona, pero
// sacarlo de iOS entero es lo único que se puede comprobar en un aparato real
// antes de enviar. Quien entraba con Google sigue entrando a SU cuenta con el
// código por correo usando su Gmail (Supabase reconoce la cuenta por el correo).
// En web y Android Google sigue igual. Volver a ofrecerlo en iOS exige antes
// probarlo en un iPad de verdad.
export const GOOGLE_LOGIN_DISPONIBLE = !(isNative && platform === 'ios');

// Bundle id de la app. Es el "clientId" del flujo nativo: Apple emite el token
// para este audience y Supabase lo verifica contra el mismo string.
const APPLE_CLIENT_ID = 'cl.mipae.app';

// El nonce es la defensa contra reutilizar un token: se manda a Apple HASHEADO
// (SHA-256 en hex, como exige ASAuthorizationAppleIDRequest) y a Supabase
// CRUDO, que lo hashea por su cuenta y compara con el que viene dentro del
// token. Mandar el crudo a los dos es el error clásico y Supabase responde
// "nonce mismatch". Si el WebView no trae crypto.subtle no se manda ninguno:
// Supabase acepta un token sin nonce, y es mejor entrar sin esa defensa que no
// poder entrar.
function nonceAleatorio() {
  const c = typeof window !== 'undefined' ? window.crypto : undefined;
  if (!c?.getRandomValues) return null;
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function sha256Hex(texto) {
  const subtle = typeof window !== 'undefined' ? window.crypto?.subtle : undefined;
  if (!subtle) return null;
  const buf = await subtle.digest('SHA-256', new TextEncoder().encode(texto));
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
}

// Apple devuelve error 1001 cuando la persona cierra la hoja sin entrar. No es
// una falla: la UI no debe mostrar nada.
const RE_CANCELADO = /1001|cancel/i;

// TERCER RECHAZO (2026-09-21): el revisor apretó "Continuar con Apple", Apple
// SÍ devolvió la credencial, y Supabase contestó "Bad ID token". Los
// `auth_logs` traían la causa real: "get keys failed: 404 Not Found". Para
// validar la firma del token, Supabase baja las llaves públicas de Apple desde
// appleid.apple.com/auth/keys, y ese endpoint responde 404 A RATOS (medido
// desde este repo: 4 de 30 sondas; hay hilos en el foro de Apple con lo mismo).
// El token en sí estaba bien: el mismo botón entró dos veces en un iPhone real
// el 19-sep. Es una falla transitoria de un tercero, así que la salida es
// pedirlo de nuevo con el MISMO token (Apple los emite válidos por minutos):
// hasta CANJE_INTENTOS veces, con una pausa corta entre medio. Si al final
// sigue fallando, se devuelve el error tal cual, que es lo que ya hacía.
//
// Se reintenta ante CUALQUIER error del canje a propósito: distinguir "404 de
// llaves" de otra cosa exige leer el texto del error, que Supabase no expone
// al cliente (llega como "Bad ID token" genérico). Dos reintentos de más ante
// un token de verdad inválido cuestan un segundo; uno de menos ante el 404
// cuesta un rechazo de Apple.
export const CANJE_INTENTOS = 3;
const CANJE_ESPERA_MS = 700;

const esperar = (ms) => new Promise((r) => setTimeout(r, ms));

async function canjearConReintento(credenciales, { esperaMs = CANJE_ESPERA_MS } = {}) {
  let ultimo;
  for (let intento = 1; intento <= CANJE_INTENTOS; intento++) {
    ultimo = await supabase.auth.signInWithIdToken(credenciales);
    if (!ultimo?.error) return ultimo;
    if (intento < CANJE_INTENTOS) await esperar(esperaMs * intento);
  }
  return ultimo;
}

export async function signInWithApple(opciones = {}) {
  if (!APPLE_LOGIN_DISPONIBLE) {
    return { data: null, error: { message: 'Iniciar sesión con Apple solo está disponible en la app de iPhone.' } };
  }

  const nonce = nonceAleatorio();
  const nonceHash = nonce ? await sha256Hex(nonce) : null;

  let respuesta;
  try {
    respuesta = await SignInWithApple.authorize({
      clientId: APPLE_CLIENT_ID,
      // Solo lo usa el flujo web del plugin; en iOS la hoja es nativa. Va
      // porque el tipo lo declara obligatorio.
      redirectURI: 'https://mipae.cl/',
      scopes: 'email name',
      ...(nonceHash ? { nonce: nonceHash } : {}),
    });
  } catch (e) {
    const message = e?.message || String(e);
    if (RE_CANCELADO.test(message)) return { data: null, error: { message, cancelado: true } };
    return { data: null, error: { message: 'No se pudo iniciar sesión con Apple. Reintenta.' } };
  }

  const r = respuesta?.response || {};
  if (!r.identityToken) {
    return { data: null, error: { message: 'Apple no devolvió una credencial. Reintenta.' } };
  }

  const { data, error } = await canjearConReintento({
    provider: 'apple',
    token: r.identityToken,
    ...(nonceHash ? { nonce } : {}),
  }, opciones);
  if (error) return { data, error };

  // Apple entrega el nombre SOLO la primera vez que la persona autoriza la app;
  // después nunca más. Si vino, se guarda en los metadatos de la sesión, que es
  // de donde App.jsx toma el nombre inicial del perfil (igual que con Google).
  // Best-effort: si falla, el onboarding pregunta el nombre de todas formas.
  const nombre = [r.givenName, r.familyName].filter(Boolean).join(' ').trim();
  if (nombre && !data?.user?.user_metadata?.full_name) {
    try { await supabase.auth.updateUser({ data: { full_name: nombre, name: nombre } }); } catch { /* opcional */ }
  }
  return { data, error: null };
}
